import { Sparkle, ArrowRight, FloppyDisk } from "@phosphor-icons/react";

function PlaceholderState() {
  return (
    <div className="h-full grid place-items-center text-center text-muted-foreground">
      <div>
        <Sparkle size={36} weight="bold" className="mx-auto text-volt mb-3" />
        <div className="font-display text-2xl font-black uppercase">Awaiting Mission Brief</div>
        <p className="text-sm mt-2">Set your parameters and generate. Drafted plan appears here.</p>
      </div>
    </div>
  );
}

function PlanHeader({ plan, onSave }) {
  return (
    <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
      <div>
        <div className="label-mono text-volt">{plan.type?.toUpperCase()}</div>
        <h2 className="font-display text-3xl font-black">{plan.name}</h2>
        <div className="text-xs text-muted-foreground mt-1">{plan.goal} · {plan.level} · {plan.weeks?.length} weeks</div>
      </div>
      <button onClick={onSave} className="bg-volt text-black px-4 py-2 font-display uppercase tracking-widest text-sm hover:bg-white inline-flex items-center gap-2" data-testid="ai-save-btn">
        <FloppyDisk size={16} weight="bold" /> Save & Edit <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function formatDayExercises(day) {
  return day.exercises?.map((ex) => {
    const detail = ex.sets ? ` ${ex.sets}x${ex.reps || ""}` : ` ${ex.reps || ""}`;
    const wt = ex.weight ? ` @ ${ex.weight}` : "";
    return `${ex.exercise_id}${detail}${wt}`;
  }).join(" · ") || "—";
}

function WeekPreview({ week: w }) {
  return (
    <div className="border border-border">
      <div className="bg-secondary px-3 py-2 flex justify-between items-center">
        <div className="font-display font-black">
          WEEK {w.week_index}
          {w.intent && <span className="text-muted-foreground text-sm ml-2">// {w.intent}</span>}
        </div>
      </div>
      <div className="divide-y divide-border">
        {w.days?.map((d, di) => (
          <div key={`d-${d.day}-${di}`} className="grid grid-cols-[80px_140px_1fr] gap-3 px-3 py-2 text-sm items-start">
            <div className="font-display font-bold text-volt">DAY {d.day}</div>
            <div>{d.focus}</div>
            <div className="text-muted-foreground">{formatDayExercises(d)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AIResult({ plan, loading, progress, onSave }) {
  return (
    <div className="border border-border bg-card p-5 min-h-[400px]">
      {!plan && !loading && <PlaceholderState />}
      {loading && (
        <div className="space-y-3" data-testid="ai-loading">
          <div className="label-mono animate-pulse text-volt">// DRAFTING PLAN...</div>
          {progress && <div className="text-sm text-muted-foreground" data-testid="ai-progress">{progress}</div>}
          <div className="h-1 bg-border overflow-hidden">
            <div className="h-full bg-volt animate-pulse" style={{ width: "60%" }} />
          </div>
          <div className="text-xs text-muted-foreground">Long mesocycles (8+ weeks) take 1–2 minutes — the AI generates them in batches with progressive overload between blocks.</div>
        </div>
      )}
      {plan && (
        <div>
          <PlanHeader plan={plan} onSave={onSave} />
          <div className="space-y-5">
            {plan.weeks?.map((w, wi) => (
              <WeekPreview key={`week-${w.week_index}-${wi}`} week={w} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
